import React, { createContext, useContext, useMemo } from 'react';

import { permissionsFor } from '@lfoc/domain/permissions.js';

/**
 * The signed-in person - /api/me: profile, roles, claims, the authenticator's
 * state and the session's limits - the API, and the two questions every
 * screen asks: may they see this screen, may they take this action.
 *
 * Those two answers come from the person's Core claims (IP-80) and are a
 * courtesy: they decide what the page offers. The server checks every claim
 * on every request, whatever the page did.
 */
const AccessContext = createContext(null);

export function AccessProvider({ principal, api, signOut, reloadPrincipal, refreshPrincipal = null, children }) {
    const value = useMemo(() => {
        const permissions = permissionsFor({ claims: principal?.claims ?? [] });
        return {
            principal,
            api,
            signOut,
            reloadPrincipal,
            // Live screens: re-read /api/me in the background, without the page
            // loader - a session hint must not unmount the screens (AuthGate.jsx).
            // Falls back to reloadPrincipal where a host gives none.
            refreshPrincipal: refreshPrincipal ?? reloadPrincipal,
            permissions,
            canView: (screen) => Boolean(permissions[screen]),
            canAct: (screen, action) => permissions[screen]?.actions.includes(action) ?? false,
        };
    }, [principal, api, signOut, reloadPrincipal, refreshPrincipal]);
    return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
}

export function useAccess() {
    const value = useContext(AccessContext);
    if (!value) throw new Error('useAccess must be used inside the AuthGate');
    return value;
}
